import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import type { Json } from '@/integrations/supabase/types';

export type MemoryType = 'preference' | 'fact' | 'pattern' | 'context';
export type MemorySource = 'explicit' | 'inferred' | 'learned';
export type OutcomeType = 'success' | 'partial' | 'failed' | 'unknown';
export type FeedbackType = 'positive' | 'negative' | 'neutral';

interface Memory {
  id: string;
  user_id: string;
  memory_type: MemoryType;
  key: string;
  value: Json;
  confidence: number;
  source: MemorySource;
  access_count: number;
  created_at: string;
  updated_at: string;
  accessed_at: string;
}

interface Interaction {
  id: string;
  user_id: string;
  session_id: string | null;
  action: string;
  input: Json;
  output: Json;
  context: Json;
  outcome: OutcomeType | null;
  feedback: FeedbackType | null;
  duration_ms: number | null;
  created_at: string;
}

interface UserModel {
  user_id: string;
  persona: Json;
  goals: Json;
  patterns: Json;
  preferences: Json;
  pain_points: Json;
  what_works: Json;
  engagement_score: number;
  last_analyzed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface AgentContext {
  memories: Memory[];
  recentHistory: Interaction[];
  userModel: UserModel | null;
  wardrobeStats: {
    totalItems: number;
    byType: Record<string, number>;
    recentlyAdded: number;
  };
  engagementMetrics: {
    daysActive: number;
    outfitsGenerated: number;
    ootdRatings: number;
    averageRating: number;
  };
}

// Session ID for grouping interactions
let currentSessionId: string | null = null;

export function useAgentMemory() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Get or create session ID
  const getSessionId = useCallback(() => {
    if (!currentSessionId) {
      currentSessionId = crypto.randomUUID();
    }
    return currentSessionId;
  }, []);

  // Remember something about the user
  const remember = useCallback(async (
    key: string, 
    value: Json, 
    type: MemoryType = 'fact',
    source: MemorySource = 'explicit',
    confidence: number = 1.0
  ) => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('agent_memory')
        .upsert({
          user_id: user.id,
          key,
          value,
          memory_type: type,
          source,
          confidence,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id,key' })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Failed to remember:', error);
      return null;
    }
  }, [user]);

  // Recall a specific memory (updates access tracking)
  const recall = useCallback(async (key: string): Promise<Memory | null> => {
    if (!user) return null;

    try {
      // First get the memory
      const { data, error } = await supabase
        .from('agent_memory')
        .select('*')
        .eq('user_id', user.id)
        .eq('key', key)
        .single();

      if (error) throw error;

      // Update access tracking
      if (data) {
        await supabase
          .from('agent_memory')
          .update({
            access_count: (data.access_count || 0) + 1,
            accessed_at: new Date().toISOString(),
          })
          .eq('id', data.id);
      }

      return data as Memory;
    } catch (error) {
      console.error('Failed to recall:', error);
      return null;
    }
  }, [user]);

  // Get all memories of a certain type
  const getMemoriesByType = useCallback(async (type: MemoryType): Promise<Memory[]> => {
    if (!user) return [];

    try {
      const { data, error } = await supabase
        .from('agent_memory')
        .select('*')
        .eq('user_id', user.id)
        .eq('memory_type', type)
        .order('confidence', { ascending: false });

      if (error) throw error;
      return (data || []) as Memory[];
    } catch (error) {
      console.error('Failed to get memories:', error);
      return [];
    }
  }, [user]);

  // Log an interaction
  const logInteraction = useCallback(async (
    action: string,
    input: Json,
    output: Json,
    context: Json = {},
    durationMs?: number
  ): Promise<string | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('interaction_history')
        .insert({
          user_id: user.id,
          session_id: getSessionId(),
          action,
          input,
          output,
          context,
          duration_ms: durationMs,
        })
        .select('id')
        .single();

      if (error) throw error;
      return data?.id || null;
    } catch (error) {
      console.error('Failed to log interaction:', error);
      return null;
    }
  }, [user, getSessionId]);

  // Record outcome for an interaction
  const recordOutcome = useCallback(async (
    interactionId: string,
    outcome: OutcomeType,
    feedback?: FeedbackType
  ) => {
    try {
      const { error } = await supabase
        .from('interaction_history')
        .update({ outcome, feedback })
        .eq('id', interactionId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Failed to record outcome:', error);
      return false;
    }
  }, []);

  // Get recent interactions
  const getRecentInteractions = useCallback(async (limit: number = 20): Promise<Interaction[]> => {
    if (!user) return [];

    try {
      const { data, error } = await supabase
        .from('interaction_history')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return (data || []) as Interaction[];
    } catch (error) {
      console.error('Failed to get interactions:', error);
      return [];
    }
  }, [user]);

  // Get or create user model
  const getUserModel = useCallback(async (): Promise<UserModel | null> => {
    if (!user) return null;

    try {
      // Try to get existing model
      const { data, error } = await supabase
        .from('user_model')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      // Create if doesn't exist
      if (!data) {
        const { data: newModel, error: insertError } = await supabase
          .from('user_model')
          .insert({ user_id: user.id })
          .select()
          .single();

        if (insertError) throw insertError;
        return newModel as UserModel;
      }

      return data as UserModel;
    } catch (error) {
      console.error('Failed to get user model:', error);
      return null;
    }
  }, [user]);

  // Update user model
  const updateUserModel = useCallback(async (updates: Partial<Omit<UserModel, 'user_id' | 'created_at'>>) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('user_model')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Failed to update user model:', error);
      return false;
    }
  }, [user]);

  // Get full context for AI calls - THE BRAIN
  const getFullContext = useCallback(async (): Promise<AgentContext | null> => {
    if (!user) return null;

    setIsLoading(true);
    try {
      // Parallel fetch all context data
      const [memories, recentHistory, userModel, wardrobeData, outfitsData] = await Promise.all([
        supabase
          .from('agent_memory')
          .select('*')
          .eq('user_id', user.id)
          .order('confidence', { ascending: false })
          .limit(50),
        supabase
          .from('interaction_history')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(20),
        getUserModel(),
        supabase
          .from('closet_items')
          .select('*')
          .eq('user_id', user.id),
        supabase
          .from('saved_outfits')
          .select('*')
          .eq('user_id', user.id),
      ]);

      // Calculate wardrobe stats
      const wardrobeItems = wardrobeData.data || [];
      const recentlyAdded = wardrobeItems.filter(item => {
        const created = new Date(item.created_at || '');
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return created > weekAgo;
      }).length;

      const byType: Record<string, number> = {};
      wardrobeItems.forEach(item => {
        byType[item.type] = (byType[item.type] || 0) + 1;
      });

      // Calculate engagement metrics from interactions
      const interactions = (recentHistory.data || []) as Interaction[];
      const outfitsGenerated = interactions.filter(i => i.action === 'generate_outfit').length;
      const ootdRatings = interactions.filter(i => i.action === 'rate_ootd').length;
      
      // Calculate days active (unique dates in last 30 days)
      const uniqueDates = new Set(
        interactions.map(i => new Date(i.created_at).toDateString())
      );

      // Get average OOTD rating from interactions with output.score
      const ratingInteractions = interactions.filter(
        i => i.action === 'rate_ootd' && (i.output as Record<string, unknown>)?.score
      );
      const averageRating = ratingInteractions.length > 0
        ? ratingInteractions.reduce((sum, i) => sum + ((i.output as Record<string, unknown>).score as number || 0), 0) / ratingInteractions.length
        : 0;

      return {
        memories: (memories.data || []) as Memory[],
        recentHistory: interactions,
        userModel,
        wardrobeStats: {
          totalItems: wardrobeItems.length,
          byType,
          recentlyAdded,
        },
        engagementMetrics: {
          daysActive: uniqueDates.size,
          outfitsGenerated,
          ootdRatings,
          averageRating,
        },
      };
    } catch (error) {
      console.error('Failed to get full context:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [user, getUserModel]);

  // Learn from an interaction - extract insights and update model
  const learn = useCallback(async (
    interactionId: string,
    outcome: OutcomeType,
    feedback?: FeedbackType
  ) => {
    if (!user) return;

    // Record the outcome
    await recordOutcome(interactionId, outcome, feedback);

    // If positive feedback, extract and remember what worked
    if (feedback === 'positive' || outcome === 'success') {
      const userModel = await getUserModel();
      if (userModel) {
        const whatWorks = [...((userModel.what_works as Json[]) || [])];
        whatWorks.push({
          interactionId,
          timestamp: new Date().toISOString(),
          outcome,
        });

        // Keep last 50 successes
        const trimmedWhatWorks = whatWorks.slice(-50);

        await updateUserModel({
          what_works: trimmedWhatWorks as Json,
          engagement_score: Math.min(1, (userModel.engagement_score || 0.5) + 0.02),
        });
      }
    }

    // If negative feedback, record pain point
    if (feedback === 'negative' || outcome === 'failed') {
      const userModel = await getUserModel();
      if (userModel) {
        const painPoints = [...((userModel.pain_points as Json[]) || [])];
        painPoints.push({
          interactionId,
          timestamp: new Date().toISOString(),
          outcome,
        });

        // Keep last 20 pain points
        const trimmedPainPoints = painPoints.slice(-20);

        await updateUserModel({
          pain_points: trimmedPainPoints as Json,
          engagement_score: Math.max(0, (userModel.engagement_score || 0.5) - 0.01),
        });
      }
    }
  }, [user, recordOutcome, getUserModel, updateUserModel]);

  // Forget a specific memory
  const forget = useCallback(async (key: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('agent_memory')
        .delete()
        .eq('user_id', user.id)
        .eq('key', key);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Failed to forget:', error);
      return false;
    }
  }, [user]);

  return {
    // Core memory operations
    remember,
    recall,
    forget,
    getMemoriesByType,
    
    // Interaction tracking
    logInteraction,
    recordOutcome,
    getRecentInteractions,
    
    // User model
    getUserModel,
    updateUserModel,
    
    // Full context (The Brain)
    getFullContext,
    
    // Learning
    learn,
    
    // Session
    getSessionId,
    
    // State
    isLoading,
  };
}
